import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import { useConfirmTossPayments } from "hooks/useConfirmTossPayments";
import useIsInappWebview from "hooks/useIsInappWebview";
import { sendMessage } from "utils/webview";
import useGetDeviceInfo from "../../hooks/useGetDeviceInfo";
import { compareVersions } from "../../logic/logics_common";
import { useRecoilValue } from "recoil";
import { userAtom } from "../../recoil/userAtom";

export default function TossPaymentsSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isInappWebview = useIsInappWebview();
  const { appVersion } = useGetDeviceInfo();
  const user = useRecoilValue(userAtom);

  const {
    mutate: confirmPayments,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useConfirmTossPayments();

  const errorMessage =
    (error && typeof error === "object" && "message" in error
      ? (error as any).message
      : undefined) || undefined;

  useEffect(() => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const payAmount = Number(searchParams.get("amount"));

    if (orderId && paymentKey && payAmount) {
      // 결제 확인 API 호출
      confirmPayments(
        { orderId, paymentKey, payAmount },
        {
          onSuccess: () => {
            // 결제 성공 시, 결제 전까지 불변이어야 했던 로컬스토리지 키 초기화
            try {
              // 결제 성공 후 재진입 시 결제 페이지의 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송을 1회 차단
              localStorage.setItem("suppress_payment_page_view", "1");
            } catch (e) {}
            if (isInappWebview) {
              if (user?.isInsider === true) {
                sendMessage(
                  JSON.stringify({
                    event: "toss_payments",
                    params: {
                      status: "success",
                    },
                  })
                );
              } else if (compareVersions(appVersion, "1.1.99") >= 0) {
                if (user?.segmentType) {
                  sendMessage(
                    JSON.stringify({
                      event: "toss_payments",
                      params: {
                        status: "success",
                        metaCapiEventList: [
                          {
                            eventId: `PST_${user?.segmentType}_${orderId}`,
                            eventName: `program_start_type_${user?.segmentType}`,
                            eventParams: {
                              content_ids: [
                                `distancing_program_type_${user?.segmentType}`,
                              ],
                              content_type: "product",
                              content_name: `디스턴싱 프로그램 Type ${user?.segmentType}`,
                              content_category: "Digital Program",
                              value: 395000,
                              currency: "KRW",
                            },
                          },
                          {
                            eventId: `PS_${orderId}`,
                            eventName: "program_start",
                            eventParams: {
                              content_ids: ["distancing_program_main"],
                              content_type: "product",
                              content_name: "디스턴싱 프로그램",
                              content_category: "Digital Program",
                              value: 395000,
                              currency: "KRW",
                            },
                          },
                          {
                            eventId: `DS_${orderId}`,
                            eventName: "debug_start",
                            byOs: true,
                            eventParams: {
                              value: 395000,
                              currency: "KRW",
                            },
                          },
                        ],
                        firebaseAnalyticsEventList: [
                          {
                            eventName: "purchase",
                            eventParams: {
                              transaction_id: orderId,
                            },
                          },
                        ],
                        appsflyerEventList: [
                          {
                            eventName: "af_purchase",
                            eventParams: {
                              af_content_id: "distancing_program",
                              af_order_id: orderId,
                            },
                          },
                        ],
                      },
                    })
                  );
                } else {
                  sendMessage(
                    JSON.stringify({
                      event: "toss_payments",
                      params: {
                        status: "success",
                        metaCapiEventList: [
                          {
                            eventId: `PS_${orderId}`,
                            eventName: "program_start",
                            eventParams: {
                              content_ids: ["distancing_program_main"],
                              content_type: "product",
                              content_name: "디스턴싱 프로그램",
                              content_category: "Digital Program",
                              value: 395000,
                              currency: "KRW",
                            },
                          },
                          {
                            eventId: `DS_${orderId}`,
                            eventName: "debug_start",
                            byOs: true,
                            eventParams: {
                              value: 395000,
                              currency: "KRW",
                            },
                          },
                        ],
                        firebaseAnalyticsEventList: [
                          {
                            eventName: "purchase",
                            eventParams: {
                              transaction_id: orderId,
                            },
                          },
                        ],
                        appsflyerEventList: [
                          {
                            eventName: "af_purchase",
                            eventParams: {
                              af_content_id: "distancing_program",
                              af_order_id: orderId,
                            },
                          },
                        ],
                      },
                    })
                  );
                }
              } else if (compareVersions(appVersion, "1.1.47") >= 0) {
                if (user?.segmentType) {
                  if (user?.segmentType === "a") {
                    sendMessage(
                      JSON.stringify({
                        event: "toss_payments",
                        params: {
                          status: "success",
                          logEventList: [
                            {
                              eventName: `program_start_type_${user?.segmentType}`,
                              value: payAmount,
                              currency: "KRW",
                            },
                            {
                              eventName: "program_start",
                              value: payAmount,
                              currency: "KRW",
                            },
                            {
                              eventName: "debug_start",
                              value: payAmount,
                              currency: "KRW",
                              byOs: true,
                            },
                          ],
                          firebaseAnalyticsEventList: [
                            {
                              eventName: "purchase",
                              eventParams: {
                                transaction_id: orderId,
                              },
                            },
                            {
                              eventName: `purchase_type_${user?.segmentType}`,
                              eventParams: {
                                transaction_id: `PT_${orderId}_${user?.segmentType}`,
                              },
                            },
                          ],
                          appsflyerEventList: [
                            {
                              eventName: "af_purchase",
                              eventParams: {
                                af_content_id: "distancing_program",
                                af_order_id: orderId,
                              },
                            },
                            {
                              eventName: "af_purchase_type_a",
                              eventParams: {
                                af_content_id: "distancing_program_type_a",
                                af_order_id: `PT_${orderId}_${user?.segmentType}`,
                              },
                            },
                          ],
                        },
                      })
                    );
                  } else {
                    sendMessage(
                      JSON.stringify({
                        event: "toss_payments",
                        params: {
                          status: "success",
                          logEventList: [
                            {
                              eventName: `program_start_type_${user?.segmentType}`,
                              value: payAmount,
                              currency: "KRW",
                            },
                            {
                              eventName: "program_start",
                              value: payAmount,
                              currency: "KRW",
                            },
                            {
                              eventName: "debug_start",
                              value: payAmount,
                              currency: "KRW",
                              byOs: true,
                            },
                          ],
                          firebaseAnalyticsEventList: [
                            {
                              eventName: "purchase",
                              eventParams: {
                                transaction_id: orderId,
                              },
                            },
                            {
                              eventName: `purchase_type_${user?.segmentType}`,
                              eventParams: {
                                transaction_id: `PT_${orderId}_${user?.segmentType}`,
                              },
                            },
                          ],
                          appsflyerEventList: [
                            {
                              eventName: "af_purchase",
                              eventParams: {
                                af_content_id: "distancing_program",
                                af_order_id: orderId,
                              },
                            },
                          ],
                        },
                      })
                    );
                  }
                } else {
                  sendMessage(
                    JSON.stringify({
                      event: "toss_payments",
                      params: {
                        status: "success",
                        logEventList: [
                          {
                            eventName: "program_start",
                            value: payAmount,
                            currency: "KRW",
                          },
                          {
                            eventName: "debug_start",
                            value: payAmount,
                            currency: "KRW",
                            byOs: true,
                          },
                        ],
                        firebaseAnalyticsEventList: [
                          {
                            eventName: "purchase",
                            eventParams: {
                              transaction_id: orderId,
                            },
                          },
                        ],
                        appsflyerEventList: [
                          {
                            eventName: "af_purchase",
                            eventParams: {
                              af_content_id: "distancing_program",
                              af_order_id: orderId,
                            },
                          },
                        ],
                      },
                    })
                  );
                }
              } else {
                sendMessage(
                  JSON.stringify({
                    event: "toss_payments",
                    params: {
                      status: "success",
                    },
                  })
                );
              }
            } else {
              // 웹앱이면 메인화면으로
              navigate("/", { replace: true });
            }
          },
        }
      );
    }
  }, []);

  // 로딩/성공/실패 UI 렌더
  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center" spacing={1.5}>
            <div>결제 처리 중 오류가 발생했습니다.</div>
            {errorMessage && <div>{errorMessage}</div>}
            <Button
              color="primary"
              onClick={() => {
                if (isInappWebview) {
                  sendMessage(
                    JSON.stringify({
                      event: "toss_payments",
                      params: {
                        status: "cancel",
                      },
                    })
                  );
                } else {
                  navigate(-1);
                }
              }}
            >
              확인
            </Button>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  return null;
}
